<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import ProjectService, { PROJECT_CONSTANT } from "../../services/projectService.js";
import { useProjectsByTab } from "../../composables/useProjectsByTab.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import { useToast } from "../../composables/useToast.js";

const props = defineProps({
  documentId: { type: String, required: true },
  sourceProjectId: { type: String, required: true },
});
const emit = defineEmits(["close", "copied"]);

const projectService = new ProjectService();
const { state: tabState, loadFirstPage, loadMore, refreshAll } = useProjectsByTab();
const toast = useToast();
const ALL = PROJECT_CONSTANT.PROJECT_TYPES.ALL;

const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => loadMore(ALL));

const selectedProjectId = ref(null);
const isCopying = ref(false);
const copyError = ref(false);

const availableProjects = computed(() =>
  (tabState[ALL]?.projects ?? []).filter((p) => p.id !== props.sourceProjectId)
);

onMounted(() => {
  loadFirstPage(ALL);
  document.addEventListener("keydown", onEsc);
});
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }

async function confirmCopy() {
  if (!selectedProjectId.value || isCopying.value) return;
  isCopying.value = true;
  copyError.value = false;
  const targetProject = availableProjects.value.find((p) => p.id === selectedProjectId.value);
  try {
    await projectService.copyDocumentToProject({
      documentId: props.documentId,
      sourceProjectId: props.sourceProjectId,
      targetProjectId: selectedProjectId.value,
      withAnnotations: true,
    });
    toast({ type: "success", title: "Document copied", message: targetProject ? `Copied to ${targetProject.name}` : "Copied successfully", position: "bottom" });
    emit("copied");
    emit("close");
    // Target project's document count is cached in the shared projects list —
    // refetch silently in the background so it's correct next time it's shown,
    // without making the user wait on it before the modal closes.
    refreshAll();
  } catch (error) {
    copyError.value = true;
    toast({ type: "error", title: "Couldn't copy document", message: error.message, position: "bottom" });
  } finally {
    isCopying.value = false;
  }
}
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/60 z-[500] flex items-center justify-center p-4" @click.self="close">
    <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[420px] flex flex-col overflow-hidden z-[520] h-[70vh] max-h-[600px]">

      <div class="flex items-center justify-between px-6 py-4 shrink-0">
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0">Copy to Project</h2>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

      <div class="flex-1 overflow-y-auto p-3">
        <div v-if="tabState[ALL]?.isLoading" class="flex justify-center py-8">
          <div class="w-5 h-5 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
        </div>
        <template v-else>
          <button
            v-for="p in availableProjects"
            :key="p.id"
            :class="selectedProjectId === p.id
              ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
              : 'text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/60'"
            class="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-left border-0 bg-transparent cursor-pointer transition-colors rounded-lg"
            @click="selectedProjectId = p.id"
          >
            <svg class="shrink-0 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="1.8" />
            </svg>
            <span class="truncate flex-1">{{ p.name }}</span>
          </button>
          <p v-if="!availableProjects.length" class="text-sm text-slate-400 dark:text-slate-500 text-center py-8 m-0">No other projects to copy to</p>

          <!-- Infinite scroll sentinel -->
          <div ref="sentinelEl" class="h-px" />
          <div v-if="tabState[ALL]?.isLoadingMore" class="flex justify-center py-3">
            <div class="w-4 h-4 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
          </div>
        </template>
      </div>

      <p v-if="copyError" class="text-xs text-red-600 dark:text-red-400 px-6 m-0 mb-2">Failed to copy the document. Please try again.</p>

      <div class="flex items-center justify-end gap-3 px-6 pb-6 pt-2">
        <button
          class="px-6 py-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="close"
        >
          Cancel
        </button>
        <button
          :disabled="!selectedProjectId || isCopying"
          class="px-6 py-2.5 bg-slate-800 dark:bg-slate-700 text-white rounded-xl text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50"
          @click="confirmCopy"
        >
          {{ isCopying ? "Copying…" : "Copy" }}
        </button>
      </div>
    </div>
  </div>
</template>
